import { AgmCoreModule, MapsAPILoader } from '@agm/core';
import { GoogleMap } from '@agm/core/services/google-maps-types';
import { NgForOf, NgIf } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { FormatMediumDatePipe } from '@date/format-medium-date.pipe';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { CandidatureService } from '@geracaoz/candidature/candidature.service';
import { ProgramEnum } from '@model/enumerations/program-enum.model';
import { ICandidatureFrontendGeracaoz } from '@model/geracaoz/candidature-frontend.model';
import { ILocation } from '@model/geracaoz/location.model';
import { IProgramEdition } from '@model/program-edition.model';
import { ProgramEditionService } from '@programs/program-edition/program-edition.service';
import { SharedDataService } from '@services/shared-data-service';
import { BreadcrumbService } from 'xng-breadcrumb';
import Geocoder = google.maps.Geocoder;

@Component({
  selector: 'jhi-geracaoz-actions',
  templateUrl: './geracaoz-actions.component.html',
  imports: [FormsModule, FormatMediumDatePipe, FontAwesomeModule, AgmCoreModule, NgForOf, NgIf],
  standalone: true,
})
export class GeracaozActionsComponent implements OnInit {
  programEnum?: ProgramEnum;
  programEnumLower?: string;
  mainImage?: string;
  mainBanner?: string;
  enum = ProgramEnum;
  mapLat = 39.69482724691313;
  mapLng = -8.130299785093573;
  zoom = 7;
  editions: IProgramEdition[] = [];
  activeEdition?: IProgramEdition;
  markers: { lat: number; lng: number; title: string }[] = [];
  map: GoogleMap | undefined;
  geocoder: Geocoder | undefined;
  candidatures: ICandidatureFrontendGeracaoz[] = [];
  candidaturesFiltered: ICandidatureFrontendGeracaoz[] = [];
  nameFilter?: string;
  locationFilter?: string;

  constructor(
    private activatedRoute: ActivatedRoute,
    private candidatureService: CandidatureService,
    private router: Router,
    private sharedDataService: SharedDataService,
    private programEditionService: ProgramEditionService,
    private breadcrumbService: BreadcrumbService,
    protected mapsApiLoader: MapsAPILoader
  ) {}

  ngOnInit(): void {
    this.breadcrumbService.set('@geracaoz', 'Geração Z');
    this.activatedRoute.data.subscribe(({ programEnum }) => {
      this.programEnum = programEnum;
      this.programEnumLower = (this.programEnum + '').toLowerCase();
      this.mainImage = '/content/images/' + this.programEnumLower + '/logotipo.png?cache=' + new Date().getMilliseconds();
      this.mainBanner = '/content/images/' + this.programEnumLower + '/front.png?cache=' + new Date().getMilliseconds();
    });
    this.mapsApiLoader.load().then(() => {
      this.geocoder = new google.maps.Geocoder();
    });
    this.programEditionService.query({ 'programEnum.equals': ProgramEnum.GERACAOZ }).subscribe(res => {
      this.editions = res.body || [];
      const editions = this.editions.filter(a => a.active === true);
      if (editions && editions.length > 0) {
        this.activeEdition = editions[0];
      }
      if (this.activeEdition && this.activeEdition.id) {
        this.candidatureService.findForFrontend(this.activeEdition.id).subscribe(res2 => {
          this.candidatures = res2.body || [];
          this.candidaturesFiltered = this.candidatures;
          this.createMarkers();
        });
      }
    });
  }

  getEdition(id?: number): string {
    if (this.editions !== null && id) {
      const editions = this.editions.filter(a => a.id === id);
      if (editions && editions.length > 0) {
        return editions[0].edition ? editions[0].edition : '';
      }
    }
    return '';
  }

  getLocations(locations: ILocation[]): string {
    let result = [];
    for (let i = 0; i < locations.length; i++) {
      if (locations[i].county) {
        result.push(locations[i].county);
      }
    }
    result = Array.from(new Set(result));
    return result.join(', ');
  }

  getLocationsArray(locations: ILocation[]): string[] {
    let result = [];
    for (let i = 0; i < locations.length; i++) {
      if (locations[i].county) {
        const county = locations[i].county!.toUpperCase();
        result.push(county || '');
      }
    }
    result = Array.from(new Set(result));
    return result;
  }

  filterCandidatures(): void {
    this.candidaturesFiltered = this.candidatures.filter(candidature => {
      let filterLocation: boolean;
      let filterName: boolean;
      if (this.nameFilter && this.nameFilter !== '') {
        if (candidature.name) {
          filterName = candidature.name.toUpperCase().includes(this.nameFilter.toUpperCase());
        } else {
          filterName = false;
        }
      } else {
        filterName = true;
      }
      if (this.locationFilter && this.locationFilter !== '') {
        if (candidature.locations) {
          filterLocation =
            this.getLocationsArray(candidature.locations).filter(s => s.includes(this.locationFilter!.toUpperCase())).length > 0;
        } else {
          filterLocation = false;
        }
      } else {
        filterLocation = true;
      }
      return filterLocation && filterName;
    });
  }

  mapReady($event: GoogleMap): void {
    this.map = $event;
  }

  createMarkers(): void {
    this.markers = [];
    for (let i = 0; i < this.candidatures.length; i++) {
      const locations = this.candidatures[i].locations;
      if (locations && locations.length > 0) {
        for (let j = 0; j < locations.length; j++) {
          if (locations[j].latitude && locations[j].longitude) {
            this.insertIntoMarkers(i, Number(locations[j].latitude), Number(locations[j].longitude));
          }
        }
      }
    }
  }

  insertIntoMarkers(i: number, latitude: number, longitude: number): void {
    this.markers.push({ lat: latitude, lng: longitude, title: this.candidatures[i].id + '' });
  }

  selectCandidature(marker: { lat: number; lng: number; title: string }): void {
    const elementById = document.getElementById(marker.title);
    if (elementById) {
      const boxes = document.getElementsByClassName('candidature-box');
      for (let i = 0; i < boxes.length; i++) {
        boxes[i].setAttribute('style', 'box-shadow: none');
      }
      elementById.setAttribute('style', 'box-shadow: rgba(67, 164, 242, 0.7) 0px 0px 10px 5px;');
      elementById.scrollIntoView();
    }
  }

  goToCandidature(candidature: ICandidatureFrontendGeracaoz): void {
    void this.router.navigate(['geracaoz', 'acoes', candidature.slug]);
  }

  back(): void {
    void this.router.navigate(['geracaoz']);
  }
}
